/**
 * CORE-PF-04: date presets for "Run KPI diagnostics".
 *
 * Pure date math, no React, so the preset rules are testable against a
 * fixed "today". Every "last N" / "this month" window ends YESTERDAY:
 * Google Ads reporting lags a day, so a window that includes today reads
 * low and the comparison against the previous period skews.
 *
 * Dates are local calendar dates formatted YYYY-MM-DD by hand — never
 * `toISOString()`, which shifts to UTC and moves the date for anyone
 * west of Greenwich in the evening.
 */
export type KpiDatePreset =
  | 'last7'
  | 'last30'
  | 'last90'
  | 'thisMonth'
  | 'lastMonth'
  | 'custom';

export const KPI_DATE_PRESETS: readonly { key: KpiDatePreset; label: string }[] = [
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'last90', label: 'Last 90 days' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'lastMonth', label: 'Last month' },
  { key: 'custom', label: 'Custom' },
];

export const DEFAULT_KPI_DATE_PRESET: KpiDatePreset = 'last30';

/**
 * Why a preset cannot be applied today, or null when it can. Drives the
 * chip's disabled state + title.
 */
export function presetUnavailableReason(
  preset: KpiDatePreset,
  today: Date = new Date(),
): string | null {
  if (preset === 'thisMonth' && today.getDate() === 1) {
    return 'No complete day this month yet';
  }
  return null;
}

export function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, days: number): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() + days);
  return out;
}

/**
 * The start/end a preset fills in, given "today". `custom` fills nothing
 * (returns null) — the operator's own dates stay as they are.
 */
export function presetRange(
  preset: KpiDatePreset,
  today: Date = new Date(),
): { start: string; end: string } | null {
  const yesterday = addDays(today, -1);
  switch (preset) {
    case 'last7':
      return { start: formatLocalDate(addDays(yesterday, -6)), end: formatLocalDate(yesterday) };
    case 'last30':
      return { start: formatLocalDate(addDays(yesterday, -29)), end: formatLocalDate(yesterday) };
    case 'last90':
      return { start: formatLocalDate(addDays(yesterday, -89)), end: formatLocalDate(yesterday) };
    case 'thisMonth': {
      // On the 1st there is no complete day this month yet (see
      // presetUnavailableReason); the chip is disabled, so this branch
      // only runs from the 2nd onward.
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: formatLocalDate(first), end: formatLocalDate(yesterday) };
    }
    case 'lastMonth': {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: formatLocalDate(first), end: formatLocalDate(last) };
    }
    case 'custom':
      return null;
    default:
      return null;
  }
}
